import { h, Component, Prop, Element, Watch } from '@stencil/core';

import { EventSubType, EventType, HomeAwayTeam, MatchPhase } from '../../../../services/interfaces';
import type { EventIcon } from '../utils';
import { FACT_EVENT_ICONS, noMinuteEvents } from '../utils';

/**
 * @part base - The component's base `<div>` wrapper.
 * @part info - The info `<div>` container.
 * @part time - The time `<div>` container.
 * @part icon - The `<pk-icon>` component.
 * @part outline - The base outline of the `<pk-icon>`.
 * @part svg - The SVG element inside the `<pk-icon>`.
 * @part end-phase - The end phase `<div>` container.
 */
@Component({
  tag: 'pk-match-fact-event',
  styleUrl: './pk-match-fact-event.scss',
  shadow: true,
})
export class PkMatchFactItem {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkMatchFactEventElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** The additional time added to a Match */
  @Prop() additionalTime?: number;

  /** The phase of the match */
  @Prop() phase?: MatchPhase;

  /** The sub type of the event */
  @Prop() subType?: EventSubType;

  /** The team that the event belongs to: home or away */
  @Prop() team?: HomeAwayTeam;

  /** The type of the event */
  @Prop() type!: EventType;

  // Prop lifecycle events
  // =======================

  @Watch('team')
  onTeamChange() {
    this.setIdentifierAlignment();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.setIdentifierAlignment();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private setIdentifierAlignment = () => {
    const identifierElem: HTMLPkIdentifierElement = this.el.querySelector('pk-identifier');
    if (!identifierElem) return;

    identifierElem.alignment = this.team === HomeAwayTeam.HOME ? 'right' : 'left';
  };

  private get eventIcon(): EventIcon {
    if (this.type === EventType.START_PHASE) {
      return FACT_EVENT_ICONS[this.type]?.[this.phase] ?? FACT_EVENT_ICONS[this.type];
    }
    if (
      this.type === EventType.GOAL &&
      (!this.subType || this.subType === EventSubType.SCORED) &&
      this.phase?.startsWith('EXTRA_TIME')
    ) {
      return FACT_EVENT_ICONS[this.type]?.[this.phase] ?? FACT_EVENT_ICONS[this.type];
    }

    return FACT_EVENT_ICONS[this.type]?.[this.subType] ?? FACT_EVENT_ICONS[this.type];
  }

  private get isEndPhase(): boolean {
    return this.type === EventType.END_PHASE;
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <div
        class={`pk-match-fact-event group relative flex w-full items-center justify-center gap-s team-${this.team}`}
        part="base"
      >
        {!this.isEndPhase && (
          <div
            class={{
              'pk-match-fact-event__info flex flex-1 overflow-hidden group-[&.team-away]:justify-start group-[&.team-home]:justify-end':
                true,
              '!hidden': noMinuteEvents.includes(this.type),
            }}
            part="info"
          >
            <slot onSlotchange={this.setIdentifierAlignment} />
          </div>
        )}
        <div class={{ 'pk-match-fact-event__middle': true, 'w-full': this.isEndPhase }}>
          {this.type === EventType.INJURY_TIME && (
            <div class="box-border flex h-l w-xl items-center justify-center rounded-full border border-solid border-ui-02 bg-[--pk-match-fact-event--background-color]">
              <span class="font-regular text-xs2 leading-m text-text-02"> +{this.additionalTime}'</span>
            </div>
          )}
          {this.isEndPhase && (
            <div class="pk-match-fact-event__end-phase flex items-center justify-center py-xs" part="end-phase">
              <slot />
            </div>
          )}
          {this.type !== EventType.INJURY_TIME && !this.isEndPhase && (
            <pk-icon
              class="pk-match-fact-event__icon flex items-center justify-center bg-[--pk-match-fact-event--background-color]"
              name={this.eventIcon.icon}
              color={this.eventIcon.color}
              part="icon"
              exportparts="outline,svg"
            />
          )}
        </div>
        {!this.isEndPhase && (
          <div
            class={{
              'pk-match-fact-event__time flex flex-1 group-[&.team-home]:justify-start group-[&.team-away]:justify-end':
                true,
              '!hidden': noMinuteEvents.includes(this.type),
            }}
            part="time"
          >
            <slot name="time" />
          </div>
        )}
      </div>
    );
  }
}
